import { DatalayerService } from '@analytics/datalayer';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { AnalyticsActionTypes } from '@analytics/models';
import { GenericModalSources } from '@legacy/common-ui-shared-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, tap } from 'rxjs/operators';

import {
  openModalFromFunnelSettingsAction,
  redirectToFunnelStartPageAction,
  setFunnelSettingsFromGraphqlErrorAction,
} from '../../funnel';
import { noFlowIdErrorAction } from '../flow-id';
import { handleBacApiErrorAction } from './api-error.actions';

@Injectable()
export class ApiErrorEffects {
  readonly #actions$ = inject(Actions);
  readonly #injector = inject(Injector);
  readonly #errorHandler = inject(ErrorHandler);

  /**
   *  Open the Chatbot modal and pass the required data to it
   */
  public readonly onBacApiError$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(handleBacApiErrorAction),
      tap((action) => {
        // Get a reference at runtime as the datalayer service depends on the environment config.
        const datalayer = this.#injector.get(DatalayerService);
        // Fire tool error
        datalayer.pushEvent({
          event: AnalyticsActionTypes.TOOL_ERROR,
          info: {
            message: `There was an API error: ${action.message}`,
            stepName: action.stepName,
            type: 'System error',
          },
        });
      }),
      mergeMap((action) =>
        action.showModal
          ? [
              openModalFromFunnelSettingsAction({
                payload: {
                  source: GenericModalSources.BAC_ERROR,
                },
              }),
            ]
          : []
      )
    )
  );

  /**
   * Redirect back to the calculate step when a 'noFlowIdError' is raised.
   */
  public readonly onNoFlowIdError$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(noFlowIdErrorAction),
      map(() => redirectToFunnelStartPageAction())
    )
  );

  /**
   * Log errors to the console when retrieved the funnel settings
   * with GraphQL fails.
   */
  public readonly onSetFunnelSettingsFromGraphQlError$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(setFunnelSettingsFromGraphqlErrorAction),
        tap(({ error }) => this.#errorHandler.handleError(error))
      ),
    { dispatch: false }
  );
}
